import {
  HistoryOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Spin,
} from 'antd';
import { debounce, find, round, sumBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { fetchAPI } from '../../utils/api';
import SearchAmazonProduct from '../SupplierPayments/SearchAmazonProduct';
import SearchJDProduct from '../SupplierPayments/SearchJDProduct';
import OrderItemAuditHistory from './OrderListV2/OrderItemAuditHistory';
import CustomProductSuppliers from './OrderCustomProductSuppliers';

class ProductSelect extends React.Component<any, any> {
  constructor(props) {
    super(props);
    this.fetchProduct = debounce(this.fetchProduct, 800);
    this.state = {
      region: this.props.region,
      data: this.props.defaultValue ? [this.props.defaultValue] : [],
      value: this.props.defaultValue,
      fetching: false,
    };
  }

  fetchProduct = async value => {
    this.setState({ data: [], fetching: true });
    // const { items } = await fetchAPI('product/list', {
    const { items } = await fetchAPI('product/list-lite', {
      payload: {
        q: value,
        _picks: ['name', 'custom_price', 'id', 'external_url', 'price', 'note'],
        filters: {
          region: [this.props.region],
        },
      },
      version: '2.0',
      method: 'POST',
    });

    this.setState({
      data: items.map(item => ({
        ...item,
        price: item.custom_price,
        cost: item.custom_price || item.price,
      })),
      fetching: false,
    });
  };

  handleChange = value => {
    this.setState({
      value,
      fetching: false,
    });
    const product = find(this.state.data, ['id', Number(value.key)]);
    this.props.onChange(product);
  };

  render() {
    const { fetching, data, value } = this.state;

    return (
      <Select
        showSearch
        labelInValue
        value={value}
        placeholder="Select product"
        notFoundContent={fetching ? <Spin size="small" /> : null}
        filterOption={false}
        onSearch={this.fetchProduct}
        onChange={this.handleChange}
        style={{ width: '100%', marginRight: 10 }}
      >
        {data?.map(d => (
          <Select.Option value={String(d.id)}>{d?.name}</Select.Option>
        ))}
      </Select>
    );
  }
}

const OrderProductList = (props: {
  form: any;
  updateTotals: () => void;
  region: string;
  fieldName:
    | 'custom_products'
    | 'standard_products'
    | 'jd_products'
    | 'amazon_products';
  categories: any;
  useRegionTax: boolean;
  formItemLayoutWithOutLabel: any;
  buttonRef: any;
  decimalPoint?: number;
  subtotalPriceAll?: number;
}) => {
  const {
    form,
    updateTotals,
    region,
    fieldName,
    categories,
    useRegionTax,
    formItemLayoutWithOutLabel,
    buttonRef,
    subtotalPriceAll,
  } = props;

  const [titleStr, setTitleStr] = useState('');
  const [historyItem, setHistoryItem] = useState<any>(null);

  useEffect(() => {
    if (fieldName === 'custom_products') {
      setTitleStr('Custom Products');
    } else if (fieldName === 'standard_products') {
      setTitleStr('Standard Products');
    } else if (fieldName === 'jd_products') {
      setTitleStr('JD Products');
    } else if (fieldName === 'amazon_products') {
      setTitleStr('Amazon JP Products');
    }
  }, [fieldName]);

  const calculateSubtotal = (
    price: number = 0,
    qty: number = 0,
    tax: number = 0
  ) => {
    const subtotal = price * qty;
    return round(subtotal * (1 + tax / 100), 2);
  };

  return (
    <Form.List name={fieldName}>
      {(fields, { add, remove }) => {
        const listProducts = form.getFieldValue(fieldName);
        const total = round(sumBy(listProducts, 'total') || 0, 2);
        const totalCost = round(sumBy(listProducts, 'total_cost') || 0, 2);
        const margin = totalCost ? round(total - totalCost, 2) : 0;
        const marinPercentage = totalCost
          ? round((margin / total) * 100, 2)
          : 0;
        updateTotals();

        // NOTE: nested form.setFields on sub-paths does NOT refresh this
        // Form.List render closure (stale listProducts => totals/summary stuck
        // at 0). Handlers must read item values live from the store and
        // re-write the whole list so the Form.List re-renders.
        const updateItem = (index, patch) => {
          const items = [...(form.getFieldValue(fieldName) || [])];
          items[index] = { ...(items[index] || {}), ...patch };
          form.setFieldsValue({ [fieldName]: items });
        };

        const onCategoryMap = async field => {
          const name = form.getFieldValue([fieldName, field.name, 'name']);
          if (name) {
            try {
              const hideLoadingCategory = message.loading(
                'Mapping product category...'
              );
              const { category } = await fetchAPI('product/category-map', {
                method: 'POST',
                version: '2.0',
                payload: {
                  names: [name],
                },
              });
              const categoryValue = find(categories.value, ['label', category]);
              form.setFields([
                {
                  name: [fieldName, field.name, 'category_item_id'],
                  value: categoryValue ? categoryValue.value : undefined,
                },
              ]);
              hideLoadingCategory();
            } catch (error) {}
          }
        };

        return (
          <ProductListingWrapper>
            <div
              className={`${fieldName} ${fields?.length === 0 ? 'none' : ''}`}
            >
              <h3>
                <ShoppingOutlined /> {titleStr}
              </h3>

              <Row style={{ marginBottom: 10 }} gutter={16}>
                <Col span={5}>
                  <b>Product</b>
                </Col>
                <Col span={4}>
                  <b>Category</b>
                </Col>
                <Col span={3}>
                  <b>Qty</b>
                </Col>
                <Col span={3}>
                  <b>Selling Price</b>
                </Col>
                <Col span={4}>
                  <b>Values</b>
                </Col>
                <Col span={3}>
                  <b>Tax (%)</b>
                </Col>
              </Row>
              {fields.map((field, index) => {
                if (!listProducts[field.name]) {
                  return null;
                }
                return (
                  <div key={field.key}>
                    <Form.Item wrapperCol={{ span: 24 }} required>
                      <Row style={{ marginBottom: 10 }} gutter={[16, 16]}>
                        <Col span={5}>
                          <Form.Item
                            {...field}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                              },
                            ]}
                            name={[field.name, 'name']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'name']}
                            noStyle
                          >
                            {fieldName === 'standard_products' && (
                              <ProductSelect
                                region={region}
                                defaultValue={listProducts[field.name]}
                                onChange={value => {
                                  const { id, ...rest } = value;
                                  const currentItem =
                                    form.getFieldValue([
                                      fieldName,
                                      field.name,
                                    ]) || {};
                                  const quantity = currentItem.quantity || 1;
                                  const taxRate =
                                    form.getFieldValue('tax_rate') ?? 0;
                                  updateItem(field.name, {
                                    name: rest.name,
                                    price: rest.price,
                                    cost: rest.cost,
                                    external_id: id,
                                    external_url: rest.external_url,
                                    quantity,
                                    tax_rate: taxRate,
                                    total: calculateSubtotal(
                                      rest.cost,
                                      quantity,
                                      taxRate
                                    ),
                                  });
                                  onCategoryMap(field);
                                }}
                              />
                            )}
                            {fieldName === 'custom_products' && (
                              <Input
                                defaultValue={
                                  form.getFieldValue(fieldName)[field.name].name
                                }
                                onInput={(value: any) => {
                                  form.setFields([
                                    {
                                      name: [fieldName, field.name, 'name'],
                                      value: value?.target?.value,
                                    },
                                  ]);
                                }}
                                onBlur={() => onCategoryMap(field)}
                              />
                            )}
                            {fieldName === 'jd_products' && (
                              <div style={{ position: 'relative' }}>
                                <SearchJDProduct
                                  quantity={listProducts[field.name]?.quantity}
                                  address={form.getFieldValue(
                                    'shipping_address'
                                  )}
                                  complete_data={true}
                                  defaultValue={
                                    listProducts[field.name]?.external_id
                                  }
                                  onChange={value => {
                                    const currentItem =
                                      form.getFieldValue([
                                        fieldName,
                                        field.name,
                                      ]) || {};
                                    const quantity = currentItem.quantity || 1;
                                    const taxRate =
                                      form.getFieldValue('tax_rate') ?? 0;
                                    updateItem(field.name, {
                                      name: `[${value.external_id}] ${value.name}`,
                                      external_url: 'jd.com',
                                      price: value.price,
                                      cost: value.price,
                                      external_id: value.external_id,
                                      quantity,
                                      tax_rate: taxRate,
                                      total: calculateSubtotal(
                                        value.price,
                                        quantity,
                                        taxRate
                                      ),
                                    });
                                  }}
                                />
                              </div>
                            )}
                            {fieldName === 'amazon_products' && (
                              <div style={{ position: 'relative' }}>
                                <SearchAmazonProduct
                                  quantity={listProducts[field.name]?.quantity}
                                  address={form.getFieldValue(
                                    'shipping_address'
                                  )}
                                  complete_data={true}
                                  defaultValue={
                                    listProducts[field.name]?.external_id
                                  }
                                  onChange={value => {
                                    const currentItem =
                                      form.getFieldValue([
                                        fieldName,
                                        field.name,
                                      ]) || {};
                                    const quantity = currentItem.quantity || 1;
                                    const taxRate =
                                      form.getFieldValue('tax_rate') ?? 0;
                                    updateItem(field.name, {
                                      name: `[${value.external_id}] ${value.name}`,
                                      external_url: 'amazon.com',
                                      price: value.price,
                                      cost: value.price,
                                      external_id: value.external_id,
                                      quantity,
                                      tax_rate: taxRate,
                                      total: calculateSubtotal(
                                        value.price,
                                        quantity,
                                        taxRate
                                      ),
                                    });
                                  }}
                                />
                              </div>
                            )}
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...field}
                            validateTrigger={['onChange', 'onBlur']}
                            name={[field.name, 'category_item_id']}
                            rules={[{ required: true }]}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'category_item_id']}
                            noStyle
                          >
                            <Select
                              options={categories.value || []}
                              placeholder="Select Category"
                              style={{ width: '100%' }}
                              onChange={value => {
                                form.setFields([
                                  {
                                    name: [
                                      fieldName,
                                      field.name,
                                      'category_item_id',
                                    ],
                                    value,
                                  },
                                ]);
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item
                            {...field}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                type: 'number',
                              },
                            ]}
                            name={[field.name, 'quantity']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'quantity']}
                            noStyle
                          >
                            <InputNumber
                              placeholder="Quantity"
                              precision={2}
                              onChange={value => {
                                // RW2026-743/744: empty/NaN must not coerce to 0
                                if (value === null || value === undefined) {
                                  return;
                                }
                                const num = Number(value);
                                if (Number.isNaN(num)) {
                                  return;
                                }
                                value = Number(num.toFixed(2));
                                const currentItem =
                                  form.getFieldValue([fieldName, field.name]) ||
                                  {};
                                const taxRate = currentItem.tax_rate || 0;
                                updateItem(field.name, {
                                  quantity: value,
                                  total: calculateSubtotal(
                                    currentItem.price,
                                    Number(value),
                                    taxRate
                                  ),
                                });
                              }}
                              parser={value =>
                                value === null || value === undefined
                                  ? ''
                                  : String(value).replace(/\$\s?|(,*)/g, '')
                              }
                              style={{ width: '100%', marginRight: 10 }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'cost']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'cost']}
                            noStyle
                          >
                            <Input
                              placeholder="Selling price"
                              onChange={e => {
                                const currentItem =
                                  form.getFieldValue([fieldName, field.name]) ||
                                  {};
                                const value = Number(
                                  Number(e.target.value).toFixed(
                                    props.decimalPoint || 2
                                  )
                                );
                                const costValue = !value ? value : value;
                                const taxRate = currentItem.tax_rate || 0;
                                updateItem(field.name, {
                                  cost: costValue,
                                  price: value,
                                  total: calculateSubtotal(
                                    costValue,
                                    currentItem.quantity,
                                    taxRate
                                  ),
                                });
                              }}
                              style={{ width: '100%', marginRight: 10 }}
                              type="number"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'total']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'total']}
                            noStyle
                          >
                            <Input
                              style={{ width: '100%', marginRight: 10 }}
                              disabled
                            />
                          </Form.Item>
                        </Col>

                        <Col span={3}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'tax_rate']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'tax_rate']}
                            noStyle
                          >
                            <Input
                              type="number"
                              style={{ width: '100%', marginRight: 10 }}
                              disabled={useRegionTax}
                              onChange={e => {
                                const currentItem =
                                  form.getFieldValue([fieldName, field.name]) ||
                                  {};
                                const value = Number(
                                  Number(e.target.value).toFixed(2)
                                );
                                updateItem(field.name, {
                                  tax_rate: value,
                                  total: calculateSubtotal(
                                    currentItem.cost,
                                    currentItem.quantity,
                                    value
                                  ),
                                });
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={2}>
                          {!!listProducts[field.name]?.id && (
                            <Button
                              style={{ marginRight: 5 }}
                              shape="circle"
                              icon={<HistoryOutlined />}
                              onClick={() =>
                                setHistoryItem(listProducts[field.name])
                              }
                            />
                          )}
                          {!!listProducts[field.name] &&
                            fieldName === 'standard_products' && (
                              <Button
                                style={{ marginRight: 5 }}
                                shape="circle"
                                icon={<LinkOutlined />}
                                target="_blank"
                                href={listProducts[field.name].external_url}
                              />
                            )}
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={async () => {
                              const doRemove = () => {
                                // Use Form.List's remove() so rc-field-form
                                // keeps its internal key map in sync with the
                                // values. Splicing the value via
                                // setFieldsValue() leaves stale keys, so the
                                // deleted row's key gets reused by the next
                                // item ('deleted item reappears' / deleted name
                                // overwrites another row).
                                remove(field.name);
                              };

                              try {
                                const item = listProducts[field.name];
                                const itemId = item?.id;
                                if (!itemId) {
                                  doRemove();
                                  return;
                                }
                                const sps = await fetchAPI(
                                  `order/item/${itemId}/check-active-spids`
                                );
                                if (sps?.length > 0) {
                                  Modal.warning({
                                    title: 'Cannot delete order item',
                                    content: (
                                      <div>
                                        <p>
                                          This order item has active Supplier
                                          Payments. Cancel them first before
                                          deleting:
                                        </p>
                                        <ul>
                                          {sps.map(sp => (
                                            <li key={sp.supplier_payment_id}>
                                              SPID #{sp.supplier_payment_id} —{' '}
                                              {sp.status} &mdash; Created by:{' '}
                                              {sp.created_by || 'N/A'}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ),
                                    okText: 'Understood',
                                  });
                                  return;
                                }
                                doRemove();
                              } catch (e) {
                                message.error(
                                  `Check failed: ${e.message || e}`
                                );
                              }
                            }}
                          />
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...field}
                            name={[field.name, 'supplier_id']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'supplier_id']}
                          >
                            <CustomProductSuppliers
                              defaultValue={
                                listProducts[field.name]?.supplier_id
                              }
                              onChange={value => {
                                form.setFields([
                                  {
                                    name: [
                                      fieldName,
                                      field.name,
                                      'supplier_id',
                                    ],
                                    value,
                                  },
                                ]);
                              }}
                            />
                          </Form.Item>
                        </Col>

                        {/* External URL */}
                        <Col span={24}>
                          <Form.Item
                            label="External URL"
                            {...field}
                            name={[field.name, 'external_url']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'external_url']}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="External URL" />
                          </Form.Item>
                        </Col>
                        {/* External ID */}
                        <Col span={24}>
                          <Form.Item
                            label="External ID"
                            {...field}
                            name={[field.name, 'external_id']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'external_id']}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="External ID" />
                          </Form.Item>
                        </Col>
                        {/* Note ID */}
                        <Col span={24}>
                          <Form.Item
                            label="Note"
                            {...field}
                            name={[field.name, 'note']}
                            // @ts-ignore
                            fieldKey={[field.fieldKey, 'note']}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="Item note" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form.Item>
                  </div>
                );
              })}
              <Form.Item
                {...formItemLayoutWithOutLabel}
                style={{ display: 'none' }}
              >
                <Button
                  ref={buttonRef}
                  type="dashed"
                  onClick={() => {
                    add({
                      tax_rate: form.getFieldValue('region_tax_rate') ?? 0,
                    });
                  }}
                >
                  <PlusOutlined /> Add Product
                </Button>

                {/* <div
                      style={{ width: '60%', marginTop: 10, textAlign: 'center' }}
                    >
                      <b>Invoice Subtotal Amount:</b>{' '}
                      {parseFloat(String(total)).toFixed(2)} {currency}
                    </div> */}
              </Form.Item>

              <Row
                style={{ marginBottom: 30 }}
                gutter={16}
                className="margin-info"
              >
                <Col span={6}>
                  <b>Subtotal (Incl. Tax)</b>: {total} <br />
                </Col>
                <Col span={6}>
                  <b>Subtotal Cost</b>: {subtotalPriceAll || totalCost} <br />
                </Col>

                <Col span={6}>
                  <b>Margin</b>: {margin} <br />
                </Col>
                <Col span={6}>
                  <b>Margin %</b>: {marinPercentage} <br />
                </Col>
              </Row>
            </div>
            <OrderItemAuditHistory
              visible={!!historyItem}
              item={historyItem}
              onClose={() => setHistoryItem(null)}
            />
          </ProductListingWrapper>
        );
      }}
    </Form.List>
  );
};

const ProductListingWrapper = styled.div`
  > div {
    padding: 15px;
    margin-top: 25px;
    border: dashed 1px #ddd;
    border-radius: 10px;
  }

  .none {
    display: none;
  }
  .margin-info {
    background: #f5f5f5;
    padding: 15px;
    font-size: 13px;
    border-radius: 5px;
  }
`;

export default OrderProductList;
